import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RestaurantMenu from "@mui/icons-material/RestaurantMenu";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LockIcon from "@mui/icons-material/Lock";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import { loginUser } from "../../redux/slices/authSlice";
import { toast } from "react-toastify";
import PaegTitle from "../../components/PageTitle";
// 🎨 Brand Theme
const BRAND = {
  primary: "#FF6B00",
  primaryDark: "#E85A00",
  gradient: "linear-gradient(135deg, #FF6B00 0%, #E85A00 100%)",
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth || {});

  const [form, setForm] = useState({
    mobile: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   const result = await dispatch(loginUser(form));
  //   if (result.type === "auth/login/fulfilled") {
  //     navigate("/owner/dashboard");
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = await dispatch(loginUser(form));

    if (result.type === "auth/login/fulfilled") {
      toast.success("Login successful");
      navigate("/owner/dashboard");
    } else {
      toast.error(
        result.payload ||
          result.error?.message ||
          "Invalid mobile number or password",
      );
    }
  };

  return (
    <>
      <PaegTitle
        title="RestaurantWala Login – Restaurant Management Dashboard"
        description="Login to your RestaurantWala account and manage your restaurant, menu, orders, billing, staff, tables and business operations."
        keywords="RestaurantWala login, restaurant management login, restaurant software login, restaurant dashboard"
      />
      <Box
        // sx={{
        //   minHeight: "100vh",
        //   background:
        //     "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--primary-light) 100%)",
        //   display: "flex",
        //   alignItems: "center",
        //   justifyContent: "center",
        //   position: "relative",
        //   overflow: "hidden",
        //   py: 4,
        // }}
         sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: BRAND.gradient,
        p: { xs: 2, sm: 3 },
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-120px",
          left: "-120px",
          width: 320,
          height: 320,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-140px",
          right: "-100px",
          width: 380,
          height: 380,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.06)",
          pointerEvents: "none",
        },
      }}
      >
        {/* Back to Home Button - Top Left */}
        <Box
          sx={{
            position: "absolute",
            top: 24,
            left: 24,
            zIndex: 10,
          }}
        >
          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              borderRadius: "50px",
              px: 2.5,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              border: "1px solid rgba(255,255,255,0.2)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.25)",
                transform: "scale(1.05)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Back to Home
          </Button>
        </Box>

        {/* Decorative Background Elements */}
        {[
          { size: 300, top: "-10%", left: "-5%", opacity: 0.08 },
          { size: 400, bottom: "-15%", right: "-8%", opacity: 0.08 },
          { size: 200, top: "30%", right: "10%", opacity: 0.06 },
        ].map((circle, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: circle.size,
              height: circle.size,
              borderRadius: "50%",
              bgcolor: "white",
              opacity: circle.opacity,
              top: circle.top,
              left: circle.left,
              bottom: circle.bottom,
              right: circle.right,
            }}
          />
        ))}

        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >
            {/* Logo & Title */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                  boxShadow: "var(--shadow-orange-hover)",
                }}
              >
                <RestaurantMenu sx={{ fontSize: 44, color: "white" }} />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "1.5rem", sm: "1.9rem" },
                  background:
                    "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 0.5,
                }}
              >
                Welcome Back
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "var(--text-light)", fontSize: "0.95rem" }}
              >
                Login to manage your restaurant
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    color: "var(--primary)",
                  },
                }}
              >
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit}>
              {/* Mobile Number Field */}
              <TextField
                fullWidth
                label="Mobile Number"
                name="mobile"
                type="tel"
                value={form.mobile}
                onChange={handleChange}
                required
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "var(--primary)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--primary)",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--primary)",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIphoneIcon sx={{ color: "var(--primary)" }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={handleChange}
                required
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "var(--primary)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--primary)",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--primary)",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "var(--primary)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{
                          "&:hover": {
                            color: "var(--primary)",
                          },
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Forgot Password */}
              <Box sx={{ textAlign: "right", mb: 3 }}>
                <Typography
                  variant="caption"
                  component={Link}
                  to="/forgot-password"
                  sx={{
                    color: "var(--primary)",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "var(--primary-dark)",
                    },
                  }}
                >
                  Forgot password?
                </Typography>
              </Box>

              {/* Submit Button */}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading}
                endIcon={
                  loading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    <ArrowForwardIcon />
                  )
                }
                sx={{
                  py: 1.5,
                  background:
                    "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                    transform: "translateY(-3px)",
                    boxShadow: "var(--shadow-orange-hover)",
                  },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "1rem",
                  boxShadow: "var(--shadow-orange)",
                  transition: "all 0.3s ease",
                }}
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </Box>

            {/* Divider */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                my: 3,
              }}
            >
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
              <Typography variant="caption" sx={{ color: "var(--text-light)" }}>
                OR
              </Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
            </Box>

            {/* Register Link */}
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "var(--text-light)" }}>
                Don't have an account?{" "}
                <Link
                  to="/register"
                  style={{
                    color: "var(--primary)",
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "var(--primary-dark)";
                    e.target.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "var(--primary)";
                    e.target.style.textDecoration = "none";
                  }}
                >
                  Register Now
                </Link>
              </Typography>
            </Box>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2" sx={{ color: "var(--text-light)" }}>
                Are you a delivery partner?{" "}
                <Link
                  to="/delivery/login"
                  style={{
                    color: "var(--primary)",
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                >
                  Login here →
                </Link>
              </Typography>
            </Box>
          </Paper>

          {/* Footer Text */}
        </Container>


        <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11,
                  fontWeight: 500,
                  zIndex: 1,
                }}
              >
                © {new Date().getFullYear()} RestaurantWala • Your Digital Restaurant Partner
              </Typography>
      </Box>
    </>
  );
};

export default Login;
